import { Button, Card, CardBody, CardHeader, Divider, Tabs, Tab, Chip } from "@nextui-org/react";
import { EyeIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import { postFetcher } from "../../../../../services/api";
import RootAdmin from "../../../../../components/layouts/RootAdmin";
import useCustomNavigate from "../../../../../hooks/useCustomNavigate";
import CustomDataGrid from "../../../../../components/elements/CustomDataGrid";
import { useMemo, useState } from "react";
import { calculateReportProgressDuration } from "../../../../../services/helpers";

export default function IndexPage() {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const { getAdminStorageDetailPath } = useCustomNavigate();

    /*
     * Nomor halaman dan jumlah baris HARUS ikut ke dalam alamat.
     *
     * Sebelumnya kedua alamat ini tetap, tanpa page maupun limit. Server
     * memakai nilai bawaannya (halaman 1, 10 baris) sehingga hanya sepuluh
     * baris pertama yang pernah dikirim, sementara kaki tabel menampilkan
     * totalRecords yang benar - 634. Menekan tombol halaman berikutnya hanya
     * mengubah tulisan di kaki tabel: SWR tidak mengambil ulang apa pun karena
     * kuncinya tidak berubah, jadi 624 data sisanya tidak pernah terjangkau.
     */
    const halaman = page + 1; // DataGrid menghitung dari 0, API dari 1

    // Fetch data penyimpanan yang perlu divalidasi (Review by Admin atau Pending)
    const { data: dataToValidate, isLoading: isLoadingToValidate } = useSWR(
        `/api/penyimpananB3/search?status=Review%20by%20Admin&status=Pending&page=${halaman}&limit=${pageSize}`,
        postFetcher
    );

    // Fetch data penyimpanan yang sudah disetujui
    const { data: dataApproved, isLoading: isLoadingApproved } = useSWR(
        `/api/penyimpananB3/search?status=Approved&status=Rejected&page=${halaman}&limit=${pageSize}`,
        postFetcher
    );

    // Kolom untuk tabel
    const columns = useMemo(() => [
        { field: 'companyName', headerName: 'Nama Perusahaan', valueGetter: (value, row) => row.company?.name ?? '-' },
        {
            field: 'locationDetails',
            headerName: 'Detail Lokasi',
            valueGetter: (params) => {
                const { province, regency, district, village } = params?.row ?? {};
                return `${village?.name || '-'}, ${district?.name || '-'}, ${regency?.name || '-'}, ${province?.name || '-'}`;
            },
        },
        { field: 'alamatGudang', headerName: 'Alamat Gudang', },
        { field: 'luasArea', headerName: 'Luas Area (M²)', },
        {
            field: 'status',
            headerName: 'Status',
            renderCell: (params) => {
                const status = params?.row?.status;

                switch (status) {
                    case 'Rejected':
                        return <Chip color="danger" variant="flat" size="sm">{status}</Chip>;
                    case 'Pending':
                        return <Chip color="warning" variant="flat" size="sm">{status}</Chip>;
                    case 'Menunggu Verifikasi':
                        return <Chip color="primary" variant="flat" size="sm">{status}</Chip>;
                    case 'Review by Admin':
                        return <Chip color="secondary" variant="flat" size="sm">{status}</Chip>;
                    case 'Approved':
                        return <Chip color="success" variant="flat" size="sm">{status}</Chip>;
                    case 'Delete':
                        return <Chip color="default" variant="flat" size="sm">{status}</Chip>;
                    default:
                        return <Chip color="default" variant="flat" size="sm">{status}</Chip>;
                }
            },
        },
        {
            field: 'progressDuration',
            headerName: 'Lama Proses',
            renderCell: (params) => {
                const hari = parseInt(calculateReportProgressDuration(params?.row, params?.row?.status === 'Approved'), 10);
                const hariDisplay = isNaN(hari) ? '-' : `${hari} hari`;
            
                if (isNaN(hari)) {
                    return <Chip color="default" variant="flat" size="sm">{hariDisplay}</Chip>;
                } else if (hari < 3) {
                    return <Chip color="success" variant="flat" size="sm">{hariDisplay}</Chip>;
                } else if (hari >= 3 && hari <= 7) {
                    return <Chip color="warning" variant="flat" size="sm">{hariDisplay}</Chip>;
                } else if (hari > 7) {
                    return <Chip color="danger" variant="flat" size="sm">{hariDisplay}</Chip>;
                } else {
                    return <Chip color="default" variant="flat" size="sm">{hariDisplay}</Chip>;
                }
            }            
        },
        {
            field: 'action',
            headerName: 'Aksi',
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <Button size='sm' onPress={() => getAdminStorageDetailPath(params?.row?.id)} color='primary' isIconOnly>
                        <EyeIcon className='size-4' />
                    </Button>
                </div>
            ),
            sortable: false,
            filterable: false,
        },
    ], [getAdminStorageDetailPath]);

    return (
        <RootAdmin>
            <Card className="w-full" radius="sm">
                <CardHeader className="flex justify-between items-center">
                    Daftar Penyimpanan / Gudang B3
                </CardHeader>
                <Divider />
                <CardBody className="">
                    <Tabs 
                        color="primary"
                        variant="underlined"
                        aria-label="tabs"
                        classNames={{
                            tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
                            cursor: "w-full",
                            tab: "max-w-fit px-1.5 h-12",
                            tabContent: "group-data-[selected=true]:font-semibold"
                        }}
                    >
                        <Tab title="Perlu Divalidasi" value="toValidate">
                            <div className="h-[500px]">
                                <CustomDataGrid
                                    data={dataToValidate?.data || []}
                                    rowCount={dataToValidate?.totalRecords || 0}
                                    isLoading={isLoadingToValidate}
                                    columns={columns}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    page={page}
                                    setPage={setPage}
                                />
                            </div>
                        </Tab>
                        <Tab title="Sudah Disetujui" value="approved">
                            <div className="h-[500px]">
                                <CustomDataGrid
                                    data={dataApproved?.data || []}
                                    rowCount={dataApproved?.totalRecords || 0}
                                    isLoading={isLoadingApproved}
                                    columns={columns}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    page={page}
                                    setPage={setPage}
                                />
                            </div>
                        </Tab>
                    </Tabs>
                </CardBody>
            </Card>
        </RootAdmin>
    );
}
